import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { FileText, Sparkles, Save, RefreshCw, Copy } from 'lucide-react';
import { API, GenerationProgress } from './shared';

const TEXT_TYPES = [
  { id: 'blog', label: 'Blog Post' }, { id: 'social_caption', label: 'Social Caption' },
  { id: 'email', label: 'Email Copy' }, { id: 'ad_copy', label: 'Ad Copy' },
  { id: 'product_description', label: 'Product Description' }, { id: 'brochure', label: 'Brochure Text' },
];
const TONES = ['professional', 'casual', 'persuasive', 'educational', 'inspirational', 'humorous'];

export function TextStudio({ onSave }) {
  const [topic, setTopic] = useState('');
  const [textType, setTextType] = useState('blog');
  const [tone, setTone] = useState('professional');
  const [wordCount, setWordCount] = useState('500');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!topic.trim()) { toast.error('Enter a topic'); return; }
    setGenerating(true); setResult(null); setProgress(0);
    setProgressMsg('Researching topic...');
    const i = setInterval(() => {
      setProgress(p => p >= 90 ? 90 : p + Math.random() * 10);
      setProgressMsg(['Structuring content...', 'Writing paragraphs...', 'Polishing language...', 'Finalizing...'][Math.floor(Math.random() * 4)]);
    }, 1200);
    try {
      const res = await axios.post(`${API}/api/creative-studio/generate/text`, { topic: topic.trim(), type: textType, tone, word_count: parseInt(wordCount) });
      clearInterval(i); setProgress(100); setProgressMsg('Done!');
      setTimeout(() => { setResult(res.data); setGenerating(false); }, 400);
    } catch (err) { clearInterval(i); toast.error(err.response?.data?.detail || 'Failed'); setGenerating(false); }
  };

  const handleSave = () => { onSave({ type: 'text', prompt: result?.topic || topic, content: result?.content, title: topic }); };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="text-studio">
      <div className="space-y-4">
        <Card className="bg-white border-gray-border"><CardContent className="pt-4 space-y-4">
          <div><Label className="text-xs font-medium">Content Type</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1">{TEXT_TYPES.map(t => (
              <button key={t.id} onClick={() => setTextType(t.id)} className={cn("px-2.5 py-2 rounded-lg border text-xs text-left transition-all",
                textType === t.id ? "border-emerald-500 bg-emerald-50 font-medium" : "border-gray-border hover:border-gray-300")} data-testid={`text-type-${t.id}`}>{t.label}</button>
            ))}</div></div>
          <div><Label className="text-xs">Topic / Keyword</Label><Input value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. Benefits of cloud computing" className="mt-1 text-sm" data-testid="text-topic" /></div>
          <div className="flex gap-3">
            <div className="flex-1"><Label className="text-xs">Tone</Label><Select value={tone} onValueChange={setTone}><SelectTrigger className="mt-1 h-8 text-xs capitalize"><SelectValue /></SelectTrigger>
              <SelectContent>{TONES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent></Select></div>
            <div className="flex-1"><Label className="text-xs">Words</Label><Select value={wordCount} onValueChange={setWordCount}><SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="250">250</SelectItem><SelectItem value="500">500</SelectItem><SelectItem value="1000">1000</SelectItem><SelectItem value="1500">1500</SelectItem></SelectContent></Select></div>
          </div>
          <Button className="w-full h-11" onClick={generate} disabled={generating || !topic.trim()} data-testid="generate-text-btn">
            <Sparkles className="h-4 w-4 mr-2" />{generating ? 'Writing...' : 'Generate Content'}</Button>
        </CardContent></Card>
      </div>
      <div className="relative">
        <GenerationProgress active={generating} progress={Math.round(progress)} message={progressMsg} />
        {result?.content ? (
          <Card className="bg-white border-gray-border" data-testid="text-result"><CardContent className="pt-4">
            <div className="flex items-center justify-between mb-3">
              <Badge className="bg-emerald-100 text-emerald-700 text-xs capitalize">{result.type?.replace('_', ' ')}</Badge>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { navigator.clipboard.writeText(result.content); toast.success('Copied!'); }}><Copy className="h-3 w-3 mr-1" />Copy</Button>
            </div>
            <div className="prose prose-sm max-h-[400px] overflow-y-auto text-sm whitespace-pre-wrap">{result.content}</div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" className="flex-1" onClick={handleSave}><Save className="h-3.5 w-3.5 mr-1" />Save to Library</Button>
              <Button size="sm" variant="ghost" onClick={() => setResult(null)}><RefreshCw className="h-3.5 w-3.5" /></Button>
            </div>
          </CardContent></Card>
        ) : !generating ? (
          <Card className="bg-white border-gray-border"><CardContent className="p-12 text-center"><FileText className="h-16 w-16 mx-auto mb-4 text-gray-200" /><p className="text-sm text-gray-medium">Your content will appear here</p></CardContent></Card>
        ) : null}
      </div>
    </div>
  );
}
